import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  BookOpen,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  History,
  Loader2,
  RefreshCw,
} from 'lucide-react'

interface CourseWithGrade {
  id: number
  name: string
  term: string
  current_score: number | null
  current_grade: string | null
  missing_count: number
}

interface StudentProfile {
  student: { id: number; name: string; canvas_id: number }
  courses: CourseWithGrade[]
  academic: {
    average_grade: number | null
    total_missing: number
    grade_updated_at: string | null
  }
}

interface AssignmentHistoryItem {
  id: number
  name: string
  course_name: string
  percentage: number
  graded_at: string | null
  is_lowest?: boolean
  is_highest?: boolean
  is_outlier?: boolean
}

interface TrendDataPoint {
  date: string
  score: number
  name: string
  course?: string
}

interface AssignmentHistoryResponse {
  student: { id: number; name: string }
  topic: string
  summary: {
    total: number
    average: number
    lowest: { score: number; id: number; name: string }
    highest: { score: number; id: number; name: string }
    trend: 'improving' | 'declining' | 'stable' | 'insufficient_data'
  } | null
  assignments: AssignmentHistoryItem[]
  trend_data: TrendDataPoint[]
}

interface HistoricalCourse {
  id: number
  canvas_id: number
  name: string
  term: string | null
  term_start: string | null
  term_end: string | null
  final_score: number | null
  final_grade: string | null
}

interface HistoricalCoursesResponse {
  student: { id: number; name: string }
  historical_courses: HistoricalCourse[]
  total: number
}

interface DashboardStudent {
  id: number
  name: string
  stats: {
    average_grade: number | null
    missing_count: number
  }
}

export default function CoursesHub() {
  const queryClient = useQueryClient()
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)

  // Grade search state
  const [searchTopic, setSearchTopic] = useState('')
  const [submittedTopic, setSubmittedTopic] = useState('')

  // Historical state
  const [showHistorical, setShowHistorical] = useState(false)

  // Fetch students list
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery<{ students: DashboardStudent[] }>({
    queryKey: ['parent-dashboard-homework'],
    queryFn: async () => {
      const response = await api.get('/api/parent/dashboard-homework')
      return response.data
    },
  })

  const students = dashboardData?.students || []

  // Auto-select if single student
  if (!selectedStudentId && students.length === 1) {
    setSelectedStudentId(students[0].id)
  }

  // Fetch student profile (courses)
  const { data: profileData, isLoading: profileLoading } = useQuery<StudentProfile>({
    queryKey: ['parent-student-detail', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}`)
      return response.data
    },
    enabled: !!selectedStudentId,
  })

  // Grade history search
  const { data: historyData, isLoading: historyLoading, isFetching } = useQuery<AssignmentHistoryResponse>({
    queryKey: ['assignment-history', selectedStudentId, submittedTopic],
    queryFn: async () => {
      const response = await api.get(
        `/api/parent/students/${selectedStudentId}/assignment-history?topic=${encodeURIComponent(submittedTopic)}`
      )
      return response.data
    },
    enabled: !!selectedStudentId && !!submittedTopic,
  })

  // Historical courses
  const { data: historicalData, isLoading: historicalLoading } = useQuery<HistoricalCoursesResponse>({
    queryKey: ['historical-courses', selectedStudentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${selectedStudentId}/historical-courses`)
      return response.data
    },
    enabled: !!selectedStudentId && showHistorical,
  })

  const syncHistoricalMutation = useMutation({
    mutationFn: async (studentId: number) => {
      const response = await api.post(`/api/parent/students/${studentId}/sync-historical`)
      return response.data
    },
    onSuccess: (_data, studentId) => {
      queryClient.invalidateQueries({ queryKey: ['historical-courses', studentId] })
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTopic.trim()) {
      setSubmittedTopic(searchTopic.trim())
    }
  }

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-500'
    if (score >= 90) return 'text-green-400'
    if (score >= 80) return 'text-blue-400'
    if (score >= 70) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-5 h-5 text-green-400" />
      case 'declining': return <TrendingDown className="w-5 h-5 text-red-400" />
      case 'stable': return <Minus className="w-5 h-5 text-gray-400" />
      default: return null
    }
  }

  const getTrendLabel = (trend: string) => {
    switch (trend) {
      case 'improving': return 'Improving'
      case 'declining': return 'Declining'
      case 'stable': return 'Stable'
      default: return 'Not enough data'
    }
  }

  if (dashboardLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="h-64 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  const courses = profileData?.courses || []
  const academic = profileData?.academic

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-quest-primary/20 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-quest-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Courses</h1>
          <p className="text-sm text-gray-400">Browse courses, grades, and grade history</p>
        </div>
      </div>

      {/* Student Selector */}
      {students.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {students.map((student) => (
            <button
              key={student.id}
              onClick={() => {
                setSelectedStudentId(student.id)
                setSubmittedTopic('')
                setSearchTopic('')
                setShowHistorical(false)
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                selectedStudentId === student.id
                  ? 'bg-quest-primary text-white'
                  : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
              }`}
            >
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {student.name.charAt(0)}
              </div>
              {student.name}
            </button>
          ))}
        </div>
      )}

      {/* Student Overview */}
      {selectedStudentId && academic && (
        <div className="flex items-center gap-6 p-4 bg-surface-card rounded-xl">
          <div>
            <span className="text-sm text-gray-400">Average</span>
            <p className={`text-3xl font-bold ${getScoreColor(academic.average_grade)}`}>
              {academic.average_grade !== null ? `${academic.average_grade}%` : '--'}
            </p>
          </div>
          {academic.total_missing > 0 && (
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{academic.total_missing} missing</span>
            </div>
          )}
        </div>
      )}

      {/* Course Cards */}
      {selectedStudentId && (
        <div>
          {profileLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-20 bg-surface-card rounded-xl"></div>
              <div className="h-20 bg-surface-card rounded-xl"></div>
            </div>
          ) : courses.length > 0 ? (
            <div className="space-y-3">
              {courses.map((course) => (
                <Link
                  key={course.id}
                  to={`/parent/courses/student/${selectedStudentId}/course/${course.id}`}
                  className="card block hover:border-quest-primary/50 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{course.name.split(' - ')[0]}</h3>
                      {course.term && (
                        <p className="text-xs text-gray-500">{course.term}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {course.missing_count > 0 && (
                        <span className="flex items-center gap-1 text-red-400 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          {course.missing_count}
                        </span>
                      )}
                      {course.current_score !== null ? (
                        <span className={`text-xl font-bold ${getScoreColor(course.current_score)}`}>
                          {course.current_grade || `${course.current_score.toFixed(1)}%`}
                        </span>
                      ) : (
                        <span className="text-xl font-bold text-gray-500">--</span>
                      )}
                      <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-quest-primary transition-colors" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="card text-center py-8">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">No courses found</p>
            </div>
          )}
        </div>
      )}

      {/* Grade History Search */}
      {selectedStudentId && (
        <div className="card bg-gradient-to-r from-quest-primary/10 to-quest-secondary/10 border-quest-primary/30">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Search className="w-5 h-5 text-quest-primary" />
            Search Grade History
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Find all grades for a topic (e.g., "spelling", "vocabulary", "math test")
          </p>
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchTopic}
              onChange={(e) => setSearchTopic(e.target.value)}
              placeholder="Enter topic to search..."
              className="flex-1 px-4 py-3 bg-surface-card border border-gray-700/50 rounded-lg focus:border-quest-primary focus:outline-none"
            />
            <button
              type="submit"
              disabled={!searchTopic.trim() || isFetching}
              className="px-6 py-3 bg-quest-primary hover:bg-quest-primary/80 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg font-medium transition-colors"
            >
              {isFetching ? 'Searching...' : 'Search'}
            </button>
          </form>

          {/* Quick Search Tags */}
          {!submittedTopic && (
            <div className="flex flex-wrap gap-2 mt-3">
              {['spelling', 'vocabulary', 'math', 'reading', 'test', 'quiz', 'homework'].map((term) => (
                <button
                  key={term}
                  onClick={() => {
                    setSearchTopic(term)
                    setSubmittedTopic(term)
                  }}
                  className="px-3 py-1.5 bg-surface-card hover:bg-surface-hover rounded-lg text-sm text-gray-300 transition-colors"
                >
                  {term}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {submittedTopic && selectedStudentId && (
        <div className="space-y-4">
          {historyLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-24 bg-surface-card rounded-xl" />
              <div className="h-64 bg-surface-card rounded-xl" />
            </div>
          ) : historyData?.summary ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card text-center py-4">
                  <p className="text-3xl font-bold">{historyData.summary.total}</p>
                  <p className="text-xs text-gray-500">Total Graded</p>
                </div>
                <div className="card text-center py-4">
                  <p className={`text-3xl font-bold ${getScoreColor(historyData.summary.average)}`}>
                    {historyData.summary.average}%
                  </p>
                  <p className="text-xs text-gray-500">Average</p>
                </div>
                <div className="card text-center py-4 border border-red-500/30 bg-red-900/10">
                  <p className="text-3xl font-bold text-red-400">{historyData.summary.lowest.score}%</p>
                  <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                    <ChevronDown className="w-3 h-3" /> Lowest
                  </p>
                </div>
                <div className="card text-center py-4 border border-green-500/30 bg-green-900/10">
                  <p className="text-3xl font-bold text-green-400">{historyData.summary.highest.score}%</p>
                  <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                    <ChevronUp className="w-3 h-3" /> Highest
                  </p>
                </div>
              </div>

              {/* Trend */}
              <div className="card flex items-center gap-3">
                {getTrendIcon(historyData.summary.trend)}
                <div>
                  <p className="font-medium">
                    "{historyData.topic}" Trend: {getTrendLabel(historyData.summary.trend)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Based on {historyData.summary.total} graded assignments
                  </p>
                </div>
              </div>

              {/* Assignment List */}
              <div className="card">
                <h3 className="text-lg font-bold mb-4">All "{historyData.topic}" Assignments</h3>
                <div className="space-y-2">
                  {historyData.assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className={`p-3 rounded-lg ${
                        assignment.is_lowest
                          ? 'bg-red-900/20 border border-red-500/30'
                          : assignment.is_highest
                            ? 'bg-green-900/20 border border-green-500/30'
                            : 'bg-surface-card/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{assignment.name}</p>
                            {assignment.is_lowest && (
                              <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 flex-shrink-0">LOWEST</span>
                            )}
                            {assignment.is_highest && (
                              <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 flex-shrink-0">HIGHEST</span>
                            )}
                            {assignment.is_outlier && !assignment.is_lowest && (
                              <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {assignment.course_name}
                            {assignment.graded_at && (
                              <> &middot; {new Date(assignment.graded_at).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                        <p className={`text-xl font-bold flex-shrink-0 ml-4 ${getScoreColor(assignment.percentage)}`}>
                          {assignment.percentage}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="card text-center py-12">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">No graded assignments found matching "{submittedTopic}"</p>
            </div>
          )}
        </div>
      )}

      {/* Historical Grades */}
      {selectedStudentId && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <History className="w-6 h-6 text-gray-400" />
              Past Courses & Grades
            </h2>
            <button
              onClick={() => setShowHistorical(!showHistorical)}
              className="text-sm text-quest-primary hover:text-quest-secondary transition-colors"
            >
              {showHistorical ? 'Hide' : 'Show'} Historical Data
            </button>
          </div>

          {showHistorical ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-surface-card/50 rounded-lg">
                <p className="text-sm text-gray-400">Fetch grades from previous semesters/terms</p>
                <button
                  onClick={() => syncHistoricalMutation.mutate(selectedStudentId)}
                  disabled={syncHistoricalMutation.isPending}
                  className="px-4 py-2 bg-quest-primary hover:bg-quest-primary/80 disabled:bg-gray-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  {syncHistoricalMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Sync Historical</>
                  )}
                </button>
              </div>

              {syncHistoricalMutation.isSuccess && (
                <div className="p-3 bg-green-900/30 border border-green-500/30 rounded-lg text-sm text-green-400">
                  Synced {syncHistoricalMutation.data.courses_synced} courses, {syncHistoricalMutation.data.assignments_synced} assignments
                </div>
              )}

              {historicalLoading && (
                <div className="animate-pulse space-y-2">
                  <div className="h-16 bg-surface-card rounded-lg" />
                  <div className="h-16 bg-surface-card rounded-lg" />
                </div>
              )}

              {historicalData && historicalData.historical_courses.length > 0 && (
                <div className="space-y-2">
                  {historicalData.historical_courses.map((course) => (
                    <div key={course.id} className="p-4 bg-surface-card/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{course.name}</p>
                          <p className="text-sm text-gray-500">
                            {course.term || 'Unknown Term'}
                            {course.term_end && (
                              <> &middot; Ended {new Date(course.term_end).toLocaleDateString()}</>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          {course.final_score !== null ? (
                            <p className={`text-2xl font-bold ${getScoreColor(course.final_score)}`}>
                              {course.final_score.toFixed(1)}%
                            </p>
                          ) : (
                            <p className="text-xl text-gray-500">--</p>
                          )}
                          {course.final_grade && (
                            <p className="text-sm text-gray-400">{course.final_grade}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {historicalData && historicalData.historical_courses.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No historical courses found</p>
                  <p className="text-sm mt-1">Click "Sync Historical" to fetch past courses</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">View grades from previous semesters and terms</p>
          )}
        </div>
      )}

      {/* No students */}
      {students.length === 0 && (
        <div className="card text-center py-12">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">No students found</p>
          <p className="text-sm text-gray-500 mt-2">Add students in Settings to view courses</p>
        </div>
      )}
    </div>
  )
}
